import { createCustomAppLifecycle, type CustomAppCloseDecision } from "./customAppLifecycle.ts";
import { createCustomAppAiChannel, type CustomAppAiConnection } from "./customAppAiChannel.ts";
import { downloadFileUrl } from "src/utils/fileDownloading.ts";
import { parseError } from "src/errorHandling/errorParsing.ts";
import {
	type CustomAppWindowContext,
	type CustomAppWindowOpenRequest,
	validateCustomAppWindowOpenRequest,
} from "./customAppWindows.ts";
import type { PropertyInputValues } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

const CONNECT_MESSAGE_TYPE = "aavo:custom-app:connect";
const QUERY_MESSAGE_TYPE = "aavo:custom-app:query";
const CANCEL_MESSAGE_TYPE = "aavo:custom-app:cancel";
const QUERY_RESULT_MESSAGE_TYPE = "aavo:custom-app:query-result";
const QUERY_ERROR_MESSAGE_TYPE = "aavo:custom-app:query-error";
const DOCUMENT_VIEW_MESSAGE_TYPE = "aavo:custom-app:document-view";
const DOCUMENT_DOWNLOAD_MESSAGE_TYPE = "aavo:custom-app:document-download";
const DOCUMENT_RESULT_MESSAGE_TYPE = "aavo:custom-app:document-result";
const DOCUMENT_ERROR_MESSAGE_TYPE = "aavo:custom-app:document-error";
const ACTION_MESSAGE_TYPE = "aavo:custom-app:actions";
const ACTION_RESULT_MESSAGE_TYPE = "aavo:custom-app:actions-result";
const ACTION_ERROR_MESSAGE_TYPE = "aavo:custom-app:actions-error";
const FUNCTION_CALL_MESSAGE_TYPE = "aavo:custom-app:function-call";
const FUNCTION_RESULT_MESSAGE_TYPE = "aavo:custom-app:function-result";
const FUNCTION_ERROR_MESSAGE_TYPE = "aavo:custom-app:function-error";
const FUNCTION_RUN_MESSAGE_TYPE = "aavo:custom-app:function-run";
const FUNCTION_ARTIFACT_MESSAGE_TYPE = "aavo:custom-app:function-artifact";
const FUNCTION_ARTIFACT_RESULT_MESSAGE_TYPE = "aavo:custom-app:function-artifact-result";
const FUNCTION_ARTIFACT_ERROR_MESSAGE_TYPE = "aavo:custom-app:function-artifact-error";
const MANAGED_FILE_MESSAGE_TYPE = "aavo:custom-app:managed-file";
const MANAGED_FILE_RESULT_MESSAGE_TYPE = "aavo:custom-app:managed-file-result";
const MANAGED_FILE_ERROR_MESSAGE_TYPE = "aavo:custom-app:managed-file-error";
const MANAGED_FILE_PATH_PREFIX = "/custom-app-runtime/managed-files/";
const UPLOAD_MESSAGE_TYPE = "aavo:custom-app:upload";
const UPLOAD_RESULT_MESSAGE_TYPE = "aavo:custom-app:upload-result";
const UPLOAD_ERROR_MESSAGE_TYPE = "aavo:custom-app:upload-error";
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;
const BROWSER_FILE_DOWNLOAD_MESSAGE_TYPE = "aavo:custom-app:browser-file-download";
const BROWSER_FILE_DOWNLOAD_RESULT_MESSAGE_TYPE = "aavo:custom-app:browser-file-download-result";
const BROWSER_FILE_DOWNLOAD_ERROR_MESSAGE_TYPE = "aavo:custom-app:browser-file-download-error";
const PART_CONFIGURATION_VALIDATE_MESSAGE_TYPE = "aavo:custom-app:part-configuration-validate";
const PART_CONFIGURATION_GUIDANCE_MESSAGE_TYPE = "aavo:custom-app:part-configuration-guidance";
const PART_CONFIGURATION_SCHEMA_MESSAGE_TYPE = "aavo:custom-app:part-configuration-schema";
const PART_CONFIGURATION_CURRENT_INPUT_MESSAGE_TYPE = "aavo:custom-app:part-configuration-current-input";
const PART_CONFIGURATION_SCHEMA_VALIDATE_MESSAGE_TYPE = "aavo:custom-app:part-configuration-schema-validate";
const VIEW_EVENT_MESSAGE_TYPE = "aavo:custom-app:view-event";
const VIEW_EVENT_RESULT_MESSAGE_TYPE = "aavo:custom-app:view-event-result";
const VIEW_EVENT_ERROR_MESSAGE_TYPE = "aavo:custom-app:view-event-error";
const STORAGE_MESSAGE_TYPE = "aavo:custom-app:storage";
const STORAGE_RESULT_MESSAGE_TYPE = "aavo:custom-app:storage-result";
const STORAGE_ERROR_MESSAGE_TYPE = "aavo:custom-app:storage-error";
const MAX_STORAGE_FILE_BYTES = 200 * 1024 * 1024;
const STORAGE_PATH_PREFIX = "/custom-app-runtime/storage/";
const WINDOWS_MESSAGE_TYPE = "aavo:custom-app:windows";
const WINDOWS_RESULT_MESSAGE_TYPE = "aavo:custom-app:windows-result";
const WINDOWS_ERROR_MESSAGE_TYPE = "aavo:custom-app:windows-error";
const WINDOW_CLOSED_MESSAGE_TYPE = "aavo:custom-app:window-closed";
const GOOGLE_MAPS_MESSAGE_TYPE = "aavo:custom-app:google-maps";
const GOOGLE_MAPS_RESULT_MESSAGE_TYPE = "aavo:custom-app:google-maps-result";
const GOOGLE_MAPS_ERROR_MESSAGE_TYPE = "aavo:custom-app:google-maps-error";
const MAX_GEOCODED_ADDRESSES = 200;
const MAX_ROUTE_STOPS = 25;
const PROTOCOL_VERSION = 1;

interface CustomAppBrowserFileDownloadMessage {
	type: typeof BROWSER_FILE_DOWNLOAD_MESSAGE_TYPE;
	requestId: string;
	file: Blob;
	fileName: string;
}

interface CustomAppUploadMessage {
	type: typeof UPLOAD_MESSAGE_TYPE;
	requestId: string;
	file: Blob;
	fileName: string;
}

type CustomAppStorageScope = "shared" | "private";

type CustomAppStorageMessage = { type: typeof STORAGE_MESSAGE_TYPE; requestId: string } & (
	| { operation: "list"; scope: CustomAppStorageScope; prefix: string }
	| { operation: "read" | "view" | "download"; scope: CustomAppStorageScope; path: string }
	| {
			operation: "write";
			scope: CustomAppStorageScope;
			path: string;
			file: Blob;
			contentType: string | null;
			ifMatch: string | null;
			createOnly: boolean;
	  }
	| { operation: "delete"; scope: CustomAppStorageScope; path: string; ifMatch: string | null }
);

type CustomAppWindowsMessage = { type: typeof WINDOWS_MESSAGE_TYPE; requestId: string } & (
	({ operation: "open" } & CustomAppWindowOpenRequest) | { operation: "close"; windowId: string }
);

type CustomAppGoogleMapsMessage = { type: typeof GOOGLE_MAPS_MESSAGE_TYPE; requestId: string } & (
	| { operation: "getConfiguration" }
	| { operation: "geocodeAddresses"; addresses: Record<string, unknown>[] }
	| { operation: "optimizeRoute"; origin: RouteLocation; destination: RouteLocation; stops: RouteLocation[] }
);

interface RouteLocation {
	lat: number;
	lng: number;
}

/**
 * The browser settings of Google Maps that the host gives to any app that asks. Only apps declaring `google_maps:use`
 * can load the Maps script with them, because the app document's content policy blocks it for other apps.
 */
export interface CustomAppGoogleMapsConfiguration {
	/** Empty when Google Maps is not configured for this Aavo installation. */
	apiKey: string;
	mapId: string;
	language: string;
	region: string;
}

/** A Google Maps failure with the code the SDK exposes to the app. */
class CustomAppGoogleMapsRequestError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
	}
}

/** Popups opened by one app instance. */
export interface CustomAppHostWindows {
	/** Returns the new window's ID, or null when the browser blocked the popup. */
	open(request: CustomAppWindowOpenRequest): string | null;
	/** Returns false when the app instance has no such open window. */
	close(windowId: string): boolean;
}

interface CustomAppStorageFileInfo {
	scope: CustomAppStorageScope;
	path: string;
	contentType: string | null;
	sizeBytes: number;
	etag: string;
	lastModified: string;
}

/** A Data Query failure with the code the SDK exposes to the app, such as a query limit code. */
class CustomAppQueryRequestError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
	}
}

/** A storage failure with the code the SDK exposes to the app. */
class CustomAppStorageRequestError extends Error {
	constructor(
		readonly code: string,
		message: string,
	) {
		super(message);
	}
}

interface CustomAppActionMessage {
	type: typeof ACTION_MESSAGE_TYPE;
	requestId: string;
	request: Record<string, unknown>;
}

interface PartConfigurationOperation {
	endpoint: string;
	description: string;
	failureCode: string;
	isRequest: (request: Record<string, unknown>) => boolean;
	isResult: (result: Record<string, unknown>) => boolean;
}

type PartConfigurationMessageType =
	| typeof PART_CONFIGURATION_VALIDATE_MESSAGE_TYPE
	| typeof PART_CONFIGURATION_GUIDANCE_MESSAGE_TYPE
	| typeof PART_CONFIGURATION_SCHEMA_MESSAGE_TYPE
	| typeof PART_CONFIGURATION_CURRENT_INPUT_MESSAGE_TYPE
	| typeof PART_CONFIGURATION_SCHEMA_VALIDATE_MESSAGE_TYPE;

const PART_CONFIGURATION_OPERATIONS: Record<PartConfigurationMessageType, PartConfigurationOperation> = {
	[PART_CONFIGURATION_VALIDATE_MESSAGE_TYPE]: {
		endpoint: "validateConfiguratorInput",
		description: "Part configuration validation",
		failureCode: "VALIDATION_FAILED",
		isRequest: (request) => isRecord(request.configuratorInput),
		isResult: (result) => Array.isArray(result.issues),
	},
	[PART_CONFIGURATION_GUIDANCE_MESSAGE_TYPE]: {
		endpoint: "getGuidance",
		description: "Part configuration guidance",
		failureCode: "GUIDANCE_FAILED",
		isRequest: (request) =>
			optionalBoolean(request.recursive) && optionalBoolean(request.includeCurrentConfiguration),
		isResult: (result) => isRecord(result.configurator),
	},
	[PART_CONFIGURATION_SCHEMA_MESSAGE_TYPE]: {
		endpoint: "getSchema",
		description: "Part configuration schema",
		failureCode: "SCHEMA_FAILED",
		isRequest: () => true,
		isResult: (result) => Array.isArray(result.properties) && isRecord(result.childModels),
	},
	[PART_CONFIGURATION_CURRENT_INPUT_MESSAGE_TYPE]: {
		endpoint: "getCurrentInput",
		description: "Part configuration current input",
		failureCode: "CURRENT_INPUT_FAILED",
		isRequest: () => true,
		isResult: (result) => Object.hasOwn(result, "valuesByPropertyName"),
	},
	[PART_CONFIGURATION_SCHEMA_VALIDATE_MESSAGE_TYPE]: {
		endpoint: "validate",
		description: "Part configuration validation",
		failureCode: "VALIDATION_FAILED",
		isRequest: (request) => isRecord(request.configuratorInput),
		isResult: (result) => Array.isArray(result.issues) && Array.isArray(result.properties),
	},
};

interface CustomAppPartConfigurationMessage {
	type: PartConfigurationMessageType;
	requestId: string;
	request: Record<string, unknown>;
}

/**
 * The Aavo views that a Custom App can replace, with the payload of each event that the app emits in them and the
 * result that the app receives when Aavo accepts the event.
 */
export interface CustomAppViewOverrideEvents {
	partConfiguratorForm: {
		/** Completes the form with the configuration's input, which the form saves as its own. */
		complete: { payload: { valuesByPropertyName: PropertyInputValues }; result: void };
	};
}

export type CustomAppOverridableView = keyof CustomAppViewOverrideEvents;

/** Returned by a view event handler when it did not accept the event, for example because the user cancelled. */
export const CUSTOM_APP_VIEW_EVENT_NOT_ACCEPTED = Symbol("notAccepted");

type ViewEvent<V extends CustomAppOverridableView> = keyof CustomAppViewOverrideEvents[V];
type ViewEventPayload<V extends CustomAppOverridableView, E extends ViewEvent<V>> =
	CustomAppViewOverrideEvents[V][E] extends { payload: infer P } ? P : never;
type ViewEventResult<V extends CustomAppOverridableView, E extends ViewEvent<V>> =
	CustomAppViewOverrideEvents[V][E] extends { result: infer R } ? R : never;

/** The view that Aavo opened an app in place of, with a handler for each of its events. */
export type CustomAppViewOverride = {
	[V in CustomAppOverridableView]: {
		view: V;
		handlers: {
			[E in ViewEvent<V>]: (
				payload: ViewEventPayload<V, E>,
			) => Promise<ViewEventResult<V, E> | typeof CUSTOM_APP_VIEW_EVENT_NOT_ACCEPTED>;
		};
	};
}[CustomAppOverridableView];

/** Checks the payload of each event when it arrives from the app. */
const VIEW_EVENT_PAYLOAD_GUARDS: {
	[V in CustomAppOverridableView]: {
		[E in ViewEvent<V>]: (payload: unknown) => payload is ViewEventPayload<V, E>;
	};
} = {
	partConfiguratorForm: {
		// The server validates the values themselves when they are saved.
		complete: (payload): payload is { valuesByPropertyName: PropertyInputValues } =>
			isRecord(payload) && isRecord(payload.valuesByPropertyName),
	},
};

interface CustomAppViewEventMessage {
	type: typeof VIEW_EVENT_MESSAGE_TYPE;
	requestId: string;
	request: { view: string; event: string; payload: unknown };
}

interface CustomAppFunctionInputFile {
	fullFileName: string;
	fileName: string;
	mimeType: string | null;
}

interface CustomAppFunctionCallMessage {
	type: typeof FUNCTION_CALL_MESSAGE_TYPE;
	requestId: string;
	functionKey: string;
	input: Record<string, unknown>;
	inputFiles?: Array<{ inputField: string; files: CustomAppFunctionInputFile[] }>;
}

type CustomAppFunctionRunMessage = { type: typeof FUNCTION_RUN_MESSAGE_TYPE; requestId: string } & (
	| {
			operation: "start";
			functionKey: string;
			input: Record<string, unknown>;
			inputFiles?: Array<{ inputField: string; files: CustomAppFunctionInputFile[] }>;
			idempotencyKey: string;
	  }
	| { operation: "get" | "cancel"; runId: number }
	| { operation: "list"; functionKey?: string; active?: boolean; limit?: number }
);

interface CustomAppFunctionArtifactMessage {
	type: typeof FUNCTION_ARTIFACT_MESSAGE_TYPE;
	requestId: string;
	artifactId: number;
	operation: "view" | "download" | "blob";
}

interface CustomAppManagedFileMessage {
	type: typeof MANAGED_FILE_MESSAGE_TYPE;
	requestId: string;
	operation: "view" | "download" | "blob";
	/** A file attached to Aavo data, named by business identifiers; see [MANAGED_FILE_SOURCES]. */
	source: Record<string, unknown> & { type: ManagedFileSourceType };
}

interface CustomAppQueryMessage {
	type: typeof QUERY_MESSAGE_TYPE;
	requestId: string;
	dataset: string;
	request: Record<string, unknown>;
}

interface CustomAppCancelMessage {
	type: typeof CANCEL_MESSAGE_TYPE;
	requestId: string;
}

interface CustomAppDocumentMessage {
	type: typeof DOCUMENT_VIEW_MESSAGE_TYPE | typeof DOCUMENT_DOWNLOAD_MESSAGE_TYPE;
	requestId: string;
	documentId?: number;
	documentRevisionId?: number;
}

interface CustomAppDocumentAccess {
	url: string;
	fileName: string;
	contentType: string;
}

export interface CustomAppHostBridgeOptions {
	input?: unknown;
	onRequestClose?: () => void;
	/** The view that Aavo opened the app in place of, read when the app connects and on each of its events. */
	getViewOverride?: () => CustomAppViewOverride | undefined;
	frame: HTMLIFrameElement;
	dataAccessToken: string;
	refreshDataAccessToken: () => Promise<string>;
	apiOrigin?: string;
	fetcher?: typeof window.fetch;
	downloadFile?: (access: CustomAppDocumentAccess) => void;
	isUserActivationActive?: () => boolean;
	/** The window the app runs in, available to the app through the SDK. */
	window: CustomAppWindowContext;
	/** The viewer's permissions, with the permissions they include, which the app checks to show or hide content. */
	viewerPermissions: readonly string[];
	windows: CustomAppHostWindows;
	googleMaps: CustomAppGoogleMapsConfiguration;
}

export interface CustomAppHostBridge {
	beforeClose(): Promise<CustomAppCloseDecision>;
	notifyClosed(): Promise<void>;
	/** AI guidance and tools the app has registered, and calls to those tools. */
	ai: CustomAppAiConnection;
	/** Tells the app that a window it opened has closed. */
	notifyWindowClosed(windowId: string): void;
	dispose(): void;
}

export function createCustomAppHostBridge(options: CustomAppHostBridgeOptions): CustomAppHostBridge {
	const apiOrigin = new URL(options.apiOrigin ?? window.location.origin);
	const fetcher = options.fetcher ?? window.fetch.bind(window);
	const requests = new Map<string, AbortController>();
	let accessToken = options.dataAccessToken;
	let refreshPromise: Promise<string> | undefined;
	let activePort: MessagePort | undefined;
	let handlingViewEvent = false;
	let connected = false;
	let disposed = false;
	const input = structuredClone(options.input ?? {});
	const windowContext = structuredClone(options.window);
	const viewerPermissions = [...options.viewerPermissions];
	const lifecycle = createCustomAppLifecycle((message) => {
		if (!activePort) throw new Error("Custom App bridge is not connected.");
		activePort.postMessage(message);
	});
	const ai = createCustomAppAiChannel(
		(message) => {
			if (!activePort) throw new Error("Custom App bridge is not connected.");
			activePort.postMessage(message);
		},
		{ isUserActivationActive },
	);

	function connect() {
		if (disposed || connected || options.frame.contentWindow == null) return;
		connected = true;

		const channel = new MessageChannel();
		const hostPort = channel.port1;
		activePort = hostPort;
		hostPort.onmessage = (event: MessageEvent<unknown>) => handleMessage(event.data, hostPort);
		hostPort.start();
		const view = options.getViewOverride?.()?.view;
		options.frame.contentWindow.postMessage(
			{
				type: CONNECT_MESSAGE_TYPE,
				version: PROTOCOL_VERSION,
				input,
				window: windowContext,
				viewOverride: view == null ? null : { view },
				viewerPermissions,
			},
			new URL(options.frame.src).origin,
			[channel.port2],
		);
	}

	async function handleMessage(message: unknown, port: MessagePort) {
		if (lifecycle.handleMessage(message)) return;
		if (disposed) return;
		if (ai.handleMessage(message)) return;
		if (isRecord(message) && message.type === "aavo:custom-app:request-close") {
			options.onRequestClose?.();
			return;
		}
		if (isCancelMessage(message)) {
			requests.get(message.requestId)?.abort();
			requests.delete(message.requestId);
			return;
		}
		if (isBrowserFileDownloadMessage(message)) {
			await handleBrowserFileDownloadMessage(message, port);
			return;
		}
		if (isUploadMessage(message)) {
			await handleUploadMessage(message, port);
			return;
		}
		if (isActionMessage(message)) {
			await handleActionMessage(message, port);
			return;
		}
		if (isPartConfigurationMessage(message)) {
			await handlePartConfigurationMessage(message, port);
			return;
		}
		if (isViewEventMessage(message)) {
			await handleViewEventMessage(message, port);
			return;
		}
		if (isFunctionCallMessage(message)) {
			await handleFunctionMessage(message.requestId, port, "call", "Function call", {
				functionKey: message.functionKey,
				input: message.input,
				inputFiles: toFunctionInputFiles(message.inputFiles),
			});
			return;
		}
		if (isFunctionRunMessage(message)) {
			await handleFunctionRunMessage(message, port);
			return;
		}
		if (isFunctionArtifactMessage(message)) {
			await handleFunctionArtifactMessage(message, port);
			return;
		}
		if (isManagedFileMessage(message)) {
			await handleManagedFileMessage(message, port);
			return;
		}
		if (isStorageMessage(message)) {
			await handleStorageMessage(message, port);
			return;
		}
		if (isWindowsMessage(message)) {
			handleWindowsMessage(message, port);
			return;
		}
		if (isGoogleMapsMessage(message)) {
			await handleGoogleMapsMessage(message, port);
			return;
		}
		if (isQueryMessage(message)) {
			await handleQueryMessage(message, port);
			return;
		}
		if (isDocumentMessage(message)) {
			await handleDocumentMessage(message, port);
			return;
		}
		postInvalidRequest(port, message);
	}

	function handleWindowsMessage(message: CustomAppWindowsMessage, port: MessagePort) {
		function fail(code: string, detail: string) {
			port.postMessage({
				type: WINDOWS_ERROR_MESSAGE_TYPE,
				requestId: message.requestId,
				error: { code, message: detail },
			});
		}
		function succeed(result: unknown) {
			port.postMessage({ type: WINDOWS_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		}
		try {
			if (message.operation === "close") return succeed(options.windows.close(message.windowId));
			const request = { params: message.params, width: message.width, height: message.height };
			const invalid = validateCustomAppWindowOpenRequest(request);
			if (invalid !== null) return fail("INVALID_REQUEST", invalid);
			const windowId = options.windows.open(request);
			if (windowId === null)
				return fail("POPUP_BLOCKED", "The browser blocked the window. Open windows from a user action.");
			succeed({ windowId });
		} catch (error) {
			fail("WINDOW_FAILED", errorMessage(error, "The window operation failed."));
		}
	}

	function downloadBlob(file: Blob, fileName: string) {
		const url = URL.createObjectURL(file);
		try {
			(options.downloadFile ?? startDownload)({ url, fileName, contentType: file.type });
		} finally {
			// Let the browser consume the object URL before releasing it.
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		}
	}

	async function handleBrowserFileDownloadMessage(message: CustomAppBrowserFileDownloadMessage, port: MessagePort) {
		function fail(code: string, detail: string) {
			port.postMessage({
				type: BROWSER_FILE_DOWNLOAD_ERROR_MESSAGE_TYPE,
				requestId: message.requestId,
				error: { code, message: detail },
			});
		}
		if (requests.has(message.requestId)) return fail("DUPLICATE_REQUEST", "The request ID is already in use.");
		if (!isUserActivationActive())
			return fail("USER_ACTIVATION_REQUIRED", "Start the download from a user action.");
		if (message.file.size > MAX_UPLOAD_BYTES) return fail("FILE_TOO_LARGE", "Files must not exceed 200 MiB.");
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			// Give queued cancellation messages a turn before starting an irreversible browser download.
			await new Promise<void>((resolve) => setTimeout(resolve, 0));
			if (controller.signal.aborted) return;
			downloadBlob(message.file, message.fileName);
			port.postMessage({
				type: BROWSER_FILE_DOWNLOAD_RESULT_MESSAGE_TYPE,
				requestId: message.requestId,
				result: null,
			});
		} catch (error) {
			fail("DOWNLOAD_FAILED", errorMessage(error, "File download failed."));
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function handleUploadMessage(message: CustomAppUploadMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postUploadError(port, message.requestId, "DUPLICATE_REQUEST", "The request ID is already in use.");
			return;
		}
		if (message.file.size > MAX_UPLOAD_BYTES) {
			postUploadError(port, message.requestId, "FILE_TOO_LARGE", "Files must not exceed 200 MiB.");
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const fullFileName = await uploadToCache(message.file, message.fileName, controller.signal);
			if (!controller.signal.aborted)
				port.postMessage({
					type: UPLOAD_RESULT_MESSAGE_TYPE,
					requestId: message.requestId,
					result: { fullFileName },
				});
		} catch (error) {
			if (!controller.signal.aborted) {
				postUploadError(port, message.requestId, "UPLOAD_FAILED", errorMessage(error, "File upload failed."));
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	/** Uploads to the app's file cache and returns the cache file name that endpoints accept as a file handle. */
	async function uploadToCache(source: Blob, fileName: string, signal: AbortSignal): Promise<string> {
		const result = await uploadMultipartFile("/api/files/cache/upload", source, fileName, signal);
		if (
			!isRecord(result) ||
			!Array.isArray(result.cacheFileNames) ||
			result.cacheFileNames.length !== 1 ||
			typeof result.cacheFileNames[0] !== "string" ||
			result.cacheFileNames[0] === ""
		) {
			throw new Error("The uploaded file handle was missing.");
		}
		return result.cacheFileNames[0];
	}

	/** Stages a managed file that only the uploading viewer can claim, for example as app storage content. */
	async function uploadManagedFile(
		source: Blob,
		fileName: string,
		signal: AbortSignal,
	): Promise<{ managedFileId: number }> {
		const result = await uploadMultipartFile("/api/files/managed/upload", source, fileName, signal);
		const handle: unknown = Array.isArray(result) && result.length === 1 ? result[0] : undefined;
		if (!isRecord(handle) || !Number.isInteger(handle.managedFileId) || Number(handle.managedFileId) <= 0)
			throw new Error("The uploaded file handle was missing.");
		return { managedFileId: Number(handle.managedFileId) };
	}

	async function uploadMultipartFile(
		path: string,
		source: Blob,
		fileName: string,
		signal: AbortSignal,
	): Promise<unknown> {
		// Chromium 153 cannot upload the cloned sandbox Blob over HTTP/1.1 (ERR_ALPN_NEGOTIATION_FAILED).
		// Reading it into a host-owned Blob gives the multipart request a buffered body.
		const file = new Blob([await source.arrayBuffer()], { type: source.type });
		signal.throwIfAborted();
		const formData = new FormData();
		formData.append("file", file, fileName);
		const send = () =>
			fetcher(new URL(path, apiOrigin), {
				method: "PUT",
				credentials: "omit",
				headers: { Authorization: `Bearer ${accessToken}` },
				body: formData,
				signal,
			});
		let response = await send();
		if (response.status === 401) {
			accessToken = await refreshToken();
			response = await send();
		}
		const result = await parseResponseBody(response);
		if (!response.ok) throw new Error(apiErrorMessage(result, response.status, "File upload"));
		return result;
	}

	async function handleActionMessage(message: CustomAppActionMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postActionError(port, message.requestId, "DUPLICATE_REQUEST", "The request ID is already in use.");
			return;
		}
		// AI tools must not change data without the user's approval, so actions are rejected during an AI call.
		if (ai.isCallInProgress()) {
			postActionError(
				port,
				message.requestId,
				"AI_TOOL_IN_PROGRESS",
				"Agent Actions cannot run while Aavo AI is calling an AI tool of this app.",
			);
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const send = () =>
				fetcher(new URL("/api/ext/erp/customApps/actions/execute", apiOrigin), {
					method: "POST",
					credentials: "omit",
					headers: {
						Authorization: `Bearer ${accessToken}`,
						"Content-Type": "application/json",
						"X-Client-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
					body: JSON.stringify(message.request),
					signal: controller.signal,
				});
			let response = await send();
			if (response.status === 401) {
				accessToken = await refreshToken();
				response = await send();
			}
			const result = await parseResponseBody(response);
			if (!response.ok) throw new Error(apiErrorMessage(result, response.status, "Action compound"));
			port.postMessage({ type: ACTION_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				postActionError(
					port,
					message.requestId,
					"ACTION_FAILED",
					errorMessage(error, "Action compound failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function handlePartConfigurationMessage(message: CustomAppPartConfigurationMessage, port: MessagePort) {
		const operation = PART_CONFIGURATION_OPERATIONS[message.type];
		if (requests.has(message.requestId)) {
			postPartConfigurationError(
				port,
				message.type,
				message.requestId,
				"DUPLICATE_REQUEST",
				"The request ID is already in use.",
			);
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const send = () =>
				fetcher(new URL(`/api/v1/erp/configurator/partConfigurations/${operation.endpoint}`, apiOrigin), {
					method: "POST",
					credentials: "omit",
					headers: {
						Authorization: `Bearer ${accessToken}`,
						"Content-Type": "application/json",
						"X-Client-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
					body: JSON.stringify({ request: message.request }),
					signal: controller.signal,
				});
			let response = await send();
			if (response.status === 401) {
				accessToken = await refreshToken();
				response = await send();
			}
			const result = await parseResponseBody(response);
			if (!response.ok) throw new Error(apiErrorMessage(result, response.status, operation.description));
			if (!isRecord(result) || !operation.isResult(result))
				throw new Error(`The ${operation.description.toLowerCase()} response was invalid.`);
			if (!controller.signal.aborted)
				port.postMessage({ type: `${message.type}-result`, requestId: message.requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				postPartConfigurationError(
					port,
					message.type,
					message.requestId,
					operation.failureCode,
					errorMessage(error, `${operation.description} failed.`),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function handleViewEventMessage(message: CustomAppViewEventMessage, port: MessagePort) {
		const { view, event, payload } = message.request;
		const fail = (code: string, detail: string) =>
			port.postMessage({
				type: VIEW_EVENT_ERROR_MESSAGE_TYPE,
				requestId: message.requestId,
				error: { code, message: detail },
			});
		const viewOverride = options.getViewOverride?.();
		if (viewOverride?.view !== view) return fail("NOT_EXPECTED", `Aavo did not open this app in place of ${view}.`);
		const handle = viewEventHandler(viewOverride, event, payload);
		if (handle == null) return fail("INVALID_REQUEST", `Invalid ${view} event ${event}.`);
		// Events hand the user's work to Aavo to act on, which AI tools must not do without the user's approval.
		if (ai.isCallInProgress())
			return fail(
				"AI_TOOL_IN_PROGRESS",
				`The app cannot emit ${event} while Aavo AI is calling an AI tool of it.`,
			);
		if (handlingViewEvent) return fail("ALREADY_HANDLING", `Aavo is still handling the app's previous event.`);
		handlingViewEvent = true;
		try {
			const result = await handle();
			if (result === CUSTOM_APP_VIEW_EVENT_NOT_ACCEPTED)
				return fail("NOT_ACCEPTED", `The ${event} was not accepted.`);
			port.postMessage({ type: VIEW_EVENT_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		} catch (error) {
			// Handlers call Aavo APIs, whose reason for rejecting the event is in the response body.
			fail("FAILED", parseError(error).message || `Handling ${event} failed.`);
		} finally {
			handlingViewEvent = false;
		}
	}

	function handleFunctionRunMessage(message: CustomAppFunctionRunMessage, port: MessagePort) {
		switch (message.operation) {
			case "start":
				return handleFunctionMessage(message.requestId, port, "start", "Function start", {
					functionKey: message.functionKey,
					input: message.input,
					inputFiles: toFunctionInputFiles(message.inputFiles),
					idempotencyKey: message.idempotencyKey,
				});
			case "get":
				return handleFunctionMessage(message.requestId, port, "getRun", "Function run request", {
					runId: message.runId,
				});
			case "cancel":
				return handleFunctionMessage(message.requestId, port, "cancelRun", "Function run cancellation", {
					runId: message.runId,
				});
			case "list":
				return handleFunctionMessage(
					message.requestId,
					port,
					"listRuns",
					"Function run request",
					{ functionKey: message.functionKey, active: message.active, limit: message.limit },
					// The SDK receives every function result as an object.
					(runs) => (Array.isArray(runs) ? { runs } : undefined),
				);
		}
	}

	async function handleFunctionMessage(
		requestId: string,
		port: MessagePort,
		endpoint: string,
		operation: string,
		body: Record<string, unknown>,
		toResult: (body: unknown) => unknown = (result) => result,
	) {
		if (requests.has(requestId)) {
			postFunctionError(port, requestId, "DUPLICATE_REQUEST", "The request ID is already in use.");
			return;
		}
		const controller = new AbortController();
		requests.set(requestId, controller);
		try {
			const send = () =>
				fetcher(new URL(`/api/ext/erp/customApps/functions/${endpoint}`, apiOrigin), {
					method: "POST",
					credentials: "omit",
					headers: {
						Authorization: `Bearer ${accessToken}`,
						"Content-Type": "application/json",
						"X-Client-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
					},
					body: JSON.stringify(body),
					signal: controller.signal,
				});
			let response = await send();
			if (response.status === 401) {
				accessToken = await refreshToken();
				response = await send();
			}
			const responseBody = await parseResponseBody(response);
			if (!response.ok) {
				postFunctionError(
					port,
					requestId,
					functionErrorCode(response.status),
					apiErrorMessage(responseBody, response.status, operation),
				);
				return;
			}
			const result = toResult(responseBody);
			if (!isRecord(result)) throw new Error(`The ${operation.toLowerCase()} result was missing.`);
			port.postMessage({ type: FUNCTION_RESULT_MESSAGE_TYPE, requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				postFunctionError(port, requestId, "FUNCTION_FAILED", errorMessage(error, `${operation} failed.`));
			}
		} finally {
			if (requests.get(requestId) === controller) requests.delete(requestId);
		}
	}

	async function handleFunctionArtifactMessage(message: CustomAppFunctionArtifactMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postFunctionArtifactError(
				port,
				message.requestId,
				"DUPLICATE_REQUEST",
				"The request ID is already in use.",
			);
			return;
		}
		if (message.operation === "download" && !isUserActivationActive()) {
			postFunctionArtifactError(
				port,
				message.requestId,
				"USER_ACTIVATION_REQUIRED",
				"File downloads must be started from a user action.",
			);
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const disposition = message.operation === "view" ? "INLINE" : "ATTACHMENT";
			let response = await sendArtifactAccessRequest(
				message.artifactId,
				disposition,
				accessToken,
				controller.signal,
			);
			if (response.status === 401) {
				accessToken = await refreshToken();
				response = await sendArtifactAccessRequest(
					message.artifactId,
					disposition,
					accessToken,
					controller.signal,
				);
			}
			const body = await parseResponseBody(response);
			if (!response.ok) {
				postFunctionArtifactError(
					port,
					message.requestId,
					artifactAccessErrorCode(response.status),
					apiErrorMessage(body, response.status, "File access"),
				);
				return;
			}
			if (!isFileAccess(body, new URL(options.frame.src).origin, "/custom-app-runtime/execution-artifacts/"))
				throw new Error("The file access response was invalid.");
			let result: unknown = null;
			if (message.operation === "view") {
				result = documentViewAccess(body);
			} else {
				const file = await fetchAccessBlob(body, controller.signal);
				if (controller.signal.aborted) return;
				if (message.operation === "download") downloadBlob(file, body.fileName);
				else result = file;
			}
			port.postMessage({ type: FUNCTION_ARTIFACT_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				postFunctionArtifactError(
					port,
					message.requestId,
					"FILE_ACCESS_FAILED",
					errorMessage(error, "File access failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function handleManagedFileMessage(message: CustomAppManagedFileMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postManagedFileError(port, message.requestId, "DUPLICATE_REQUEST", "The request ID is already in use.");
			return;
		}
		if (message.operation === "download" && !isUserActivationActive()) {
			postManagedFileError(
				port,
				message.requestId,
				"USER_ACTIVATION_REQUIRED",
				"File downloads must be started from a user action.",
			);
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const disposition = message.operation === "view" ? "INLINE" : "ATTACHMENT";
			let response = await sendManagedFileAccessRequest(
				message.source,
				disposition,
				accessToken,
				controller.signal,
			);
			if (response.status === 401) {
				accessToken = await refreshToken();
				response = await sendManagedFileAccessRequest(
					message.source,
					disposition,
					accessToken,
					controller.signal,
				);
			}
			const body = await parseResponseBody(response);
			if (!response.ok) {
				postManagedFileError(
					port,
					message.requestId,
					artifactAccessErrorCode(response.status),
					apiErrorMessage(body, response.status, "File access"),
				);
				return;
			}
			if (!isFileAccess(body, new URL(options.frame.src).origin, MANAGED_FILE_PATH_PREFIX))
				throw new Error("The file access response was invalid.");
			let result: unknown = null;
			if (message.operation === "view") {
				result = documentViewAccess(body);
			} else {
				const file = await fetchAccessBlob(body, controller.signal);
				if (controller.signal.aborted) return;
				if (message.operation === "download") downloadBlob(file, body.fileName);
				else result = file;
			}
			port.postMessage({ type: MANAGED_FILE_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				postManagedFileError(
					port,
					message.requestId,
					"FILE_ACCESS_FAILED",
					errorMessage(error, "File access failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	function sendManagedFileAccessRequest(
		source: CustomAppManagedFileMessage["source"],
		disposition: string,
		token: string,
		signal: AbortSignal,
	) {
		const { type, ...request } = source;
		return fetcher(new URL(MANAGED_FILE_SOURCES[type].endpoint, apiOrigin), {
			method: "POST",
			credentials: "omit",
			headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
			body: JSON.stringify({ request, disposition }),
			signal,
		});
	}

	async function handleStorageMessage(message: CustomAppStorageMessage, port: MessagePort) {
		function fail(code: string, detail: string) {
			port.postMessage({
				type: STORAGE_ERROR_MESSAGE_TYPE,
				requestId: message.requestId,
				error: { code, message: detail },
			});
		}
		if (requests.has(message.requestId)) return fail("DUPLICATE_REQUEST", "The request ID is already in use.");
		if (message.operation === "download" && !isUserActivationActive())
			return fail("USER_ACTIVATION_REQUIRED", "File downloads must be started from a user action.");
		// AI tools must not change what every viewer sees without the user's approval.
		if (
			(message.operation === "write" || message.operation === "delete") &&
			message.scope === "shared" &&
			ai.isCallInProgress()
		)
			return fail(
				"AI_TOOL_IN_PROGRESS",
				"Shared app storage cannot change while Aavo AI is calling an AI tool of this app.",
			);
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const result = await executeStorageRequest(message, controller.signal);
			if (!controller.signal.aborted)
				port.postMessage({ type: STORAGE_RESULT_MESSAGE_TYPE, requestId: message.requestId, result });
		} catch (error) {
			if (!controller.signal.aborted) {
				fail(
					error instanceof CustomAppStorageRequestError ? error.code : "STORAGE_FAILED",
					errorMessage(error, "The app storage request failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function executeStorageRequest(message: CustomAppStorageMessage, signal: AbortSignal): Promise<unknown> {
		switch (message.operation) {
			case "list": {
				const body = await sendStorageRequest(
					"list",
					{ scope: message.scope.toUpperCase(), prefix: message.prefix },
					signal,
				);
				if (!isRecord(body) || !Array.isArray(body.files) || typeof body.hasMore !== "boolean")
					throw new Error("The app storage list was invalid.");
				return { files: body.files.map(toStorageFileInfo), hasMore: body.hasMore };
			}
			case "read":
			case "view":
			case "download": {
				const body = await sendStorageRequest(
					"createAccess",
					{
						scope: message.scope.toUpperCase(),
						path: message.path,
						disposition: message.operation === "view" ? "INLINE" : "ATTACHMENT",
					},
					signal,
				);
				if (body === null) {
					if (message.operation === "read") return null;
					throw new CustomAppStorageRequestError(
						"NOT_FOUND",
						`App storage file '${message.path}' was not found.`,
					);
				}
				if (
					!isRecord(body) ||
					!isFileAccess(body.access, new URL(options.frame.src).origin, STORAGE_PATH_PREFIX)
				)
					throw new Error("The file access response was invalid.");
				const file = toStorageFileInfo(body.file);
				if (message.operation === "view") return documentViewAccess(body.access);
				const blob = await fetchAccessBlob(body.access, signal);
				if (message.operation === "read") return { file, blob };
				signal.throwIfAborted();
				downloadBlob(blob, body.access.fileName);
				return null;
			}
			case "write": {
				if (message.file.size > MAX_STORAGE_FILE_BYTES)
					throw new CustomAppStorageRequestError(
						"FILE_TOO_LARGE",
						"App storage files must not exceed 200 MiB.",
					);
				const content = await uploadManagedFile(message.file, message.path.split("/").pop() ?? "file", signal);
				const body = await sendStorageRequest(
					message.scope === "shared" ? "writeShared" : "writePrivate",
					{
						path: message.path,
						content,
						contentType: message.contentType,
						ifMatch: message.ifMatch,
						createOnly: message.createOnly,
					},
					signal,
				);
				return toStorageFileInfo(body);
			}
			case "delete": {
				const body = await sendStorageRequest(
					message.scope === "shared" ? "deleteShared" : "deletePrivate",
					{ path: message.path, ifMatch: message.ifMatch },
					signal,
				);
				if (typeof body !== "boolean") throw new Error("The app storage delete result was invalid.");
				return body;
			}
		}
	}

	async function handleGoogleMapsMessage(message: CustomAppGoogleMapsMessage, port: MessagePort) {
		function fail(code: string, detail: string) {
			port.postMessage({
				type: GOOGLE_MAPS_ERROR_MESSAGE_TYPE,
				requestId: message.requestId,
				error: { code, message: detail },
			});
		}
		if (requests.has(message.requestId)) return fail("DUPLICATE_REQUEST", "The request ID is already in use.");
		if (options.googleMaps.apiKey === "")
			return fail("UNAVAILABLE", "Google Maps is not configured for this Aavo installation.");
		if (message.operation === "getConfiguration") {
			port.postMessage({
				type: GOOGLE_MAPS_RESULT_MESSAGE_TYPE,
				requestId: message.requestId,
				result: { ...options.googleMaps },
			});
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		const geocoding = message.operation === "geocodeAddresses";
		try {
			const result =
				geocoding ?
					{ coordinates: await sendGeocodingRequest(message.addresses, controller.signal) }
				:	await sendRouteOptimizationRequest(message, controller.signal);
			if (!controller.signal.aborted)
				port.postMessage({
					type: GOOGLE_MAPS_RESULT_MESSAGE_TYPE,
					requestId: message.requestId,
					result,
				});
		} catch (error) {
			if (!controller.signal.aborted) {
				fail(
					error instanceof CustomAppGoogleMapsRequestError ? error.code
					: geocoding ? "GEOCODING_FAILED"
					: "ROUTE_OPTIMIZATION_FAILED",
					errorMessage(error, geocoding ? "Geocoding failed." : "Route optimization failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function sendGeocodingRequest(addresses: Record<string, unknown>[], signal: AbortSignal) {
		const result = await sendGoogleMapsRequest({
			endpoint: "geocodeAddresses",
			body: { addresses },
			signal,
			operationName: "Geocoding",
			failureCode: "GEOCODING_FAILED",
		});
		if (!Array.isArray(result) || result.length !== addresses.length || !result.every(isCoordinatesOrNull))
			throw new Error("The geocoding response was invalid.");
		return result;
	}

	async function sendRouteOptimizationRequest(
		{ origin, destination, stops }: { origin: RouteLocation; destination: RouteLocation; stops: RouteLocation[] },
		signal: AbortSignal,
	) {
		const result = await sendGoogleMapsRequest({
			endpoint: "optimizeRoute",
			body: { origin, destination, stops },
			signal,
			operationName: "Route optimization",
			failureCode: "ROUTE_OPTIMIZATION_FAILED",
		});
		if (!isOptimizedRoute(result, stops.length)) throw new Error("The route optimization response was invalid.");
		return result;
	}

	/** Calls a Custom App Google Maps endpoint; failures carry the endpoint's error code. */
	async function sendGoogleMapsRequest({
		endpoint,
		body,
		signal,
		operationName,
		failureCode,
	}: {
		endpoint: string;
		body: Record<string, unknown>;
		signal: AbortSignal;
		operationName: string;
		failureCode: string;
	}): Promise<unknown> {
		const send = () =>
			fetcher(new URL(`/api/ext/erp/customApps/googleMaps/${endpoint}`, apiOrigin), {
				method: "POST",
				credentials: "omit",
				headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
				body: JSON.stringify(body),
				signal,
			});
		let response = await send();
		if (response.status === 401) {
			accessToken = await refreshToken();
			response = await send();
		}
		const result = await parseResponseBody(response);
		if (!response.ok) {
			// Missing declared permissions are rejected before the endpoint, without an error code.
			const fallbackCode = response.status === 403 ? "FORBIDDEN" : failureCode;
			throw new CustomAppGoogleMapsRequestError(
				isRecord(result) && typeof result.code === "string" ? result.code : fallbackCode,
				apiErrorMessage(result, response.status, operationName),
			);
		}
		return result;
	}

	/** Calls the app storage endpoint; failures carry the endpoint's storage error code. */
	async function sendStorageRequest(endpoint: string, body: Record<string, unknown>, signal: AbortSignal) {
		const send = () =>
			fetcher(new URL(`/api/ext/erp/customApps/storage/${endpoint}`, apiOrigin), {
				method: "POST",
				credentials: "omit",
				headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
				body: JSON.stringify(body),
				signal,
			});
		let response = await send();
		if (response.status === 401) {
			accessToken = await refreshToken();
			response = await send();
		}
		const result = await parseResponseBody(response);
		if (!response.ok) {
			throw new CustomAppStorageRequestError(
				isRecord(result) && typeof result.code === "string" ? result.code : "STORAGE_FAILED",
				apiErrorMessage(result, response.status, "App storage request"),
			);
		}
		return result;
	}

	function sendArtifactAccessRequest(artifactId: number, disposition: string, token: string, signal: AbortSignal) {
		return fetcher(new URL("/api/ext/erp/customApps/functions/createArtifactAccess", apiOrigin), {
			method: "POST",
			credentials: "omit",
			headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
			body: JSON.stringify({ artifactId, disposition }),
			signal,
		});
	}

	async function fetchAccessBlob(access: CustomAppDocumentAccess, signal: AbortSignal): Promise<Blob> {
		const response = await fetcher(access.url, { credentials: "omit", signal });
		if (!response.ok) {
			const detail =
				response.status === 404 ?
					"The file could not be found. It may have been removed."
				:	`The file could not be downloaded (HTTP ${response.status}).`;
			throw new Error(`${access.fileName}: ${detail}`);
		}
		return response.blob();
	}

	async function handleQueryMessage(message: CustomAppQueryMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postError(port, message.requestId, "DUPLICATE_REQUEST", "The query request ID is already in use.");
			return;
		}

		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const result = await executeQuery(message, controller.signal);
			port.postMessage({
				type: QUERY_RESULT_MESSAGE_TYPE,
				requestId: message.requestId,
				result,
			});
		} catch (error) {
			if (!controller.signal.aborted) {
				const code = error instanceof CustomAppQueryRequestError ? error.code : "QUERY_FAILED";
				postError(port, message.requestId, code, errorMessage(error));
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	async function handleDocumentMessage(message: CustomAppDocumentMessage, port: MessagePort) {
		if (requests.has(message.requestId)) {
			postDocumentError(port, message.requestId, "DUPLICATE_REQUEST", "The request ID is already in use.");
			return;
		}
		if (message.type === DOCUMENT_DOWNLOAD_MESSAGE_TYPE && !isUserActivationActive()) {
			postDocumentError(
				port,
				message.requestId,
				"USER_ACTIVATION_REQUIRED",
				"Document downloads must be started from a user action.",
			);
			return;
		}
		const controller = new AbortController();
		requests.set(message.requestId, controller);
		try {
			const access = await executeDocumentRequest(message, controller.signal);
			if (message.type === DOCUMENT_DOWNLOAD_MESSAGE_TYPE) {
				const file = await fetchAccessBlob(access, controller.signal);
				if (controller.signal.aborted) return;
				downloadBlob(file, access.fileName);
				port.postMessage({ type: DOCUMENT_RESULT_MESSAGE_TYPE, requestId: message.requestId, result: null });
			} else {
				port.postMessage({
					type: DOCUMENT_RESULT_MESSAGE_TYPE,
					requestId: message.requestId,
					result: documentViewAccess(access),
				});
			}
		} catch (error) {
			if (!controller.signal.aborted) {
				postDocumentError(
					port,
					message.requestId,
					"DOCUMENT_ACCESS_FAILED",
					errorMessage(error, "Document access failed."),
				);
			}
		} finally {
			if (requests.get(message.requestId) === controller) requests.delete(message.requestId);
		}
	}

	function isUserActivationActive() {
		return options.isUserActivationActive?.() ?? navigator.userActivation?.isActive === true;
	}

	async function executeDocumentRequest(
		message: CustomAppDocumentMessage,
		signal: AbortSignal,
	): Promise<CustomAppDocumentAccess> {
		let response = await sendDocumentRequest(message, accessToken, signal);
		if (response.status === 401) {
			accessToken = await refreshToken();
			response = await sendDocumentRequest(message, accessToken, signal);
		}
		const body = await parseResponseBody(response);
		if (!response.ok) throw new Error(apiErrorMessage(body, response.status, "Document access"));
		if (!isFileAccess(body, new URL(options.frame.src).origin, "/custom-app-runtime/documents/"))
			throw new Error("The document access response was invalid.");
		return body;
	}

	function sendDocumentRequest(message: CustomAppDocumentMessage, token: string, signal: AbortSignal) {
		const url = new URL("/api/ext/erp/customApps/documents/createAccess", apiOrigin);
		return fetcher(url, {
			method: "POST",
			credentials: "omit",
			headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
			body: JSON.stringify({
				documentId: message.documentId ?? null,
				documentRevisionId: message.documentRevisionId ?? null,
				disposition: message.type === DOCUMENT_VIEW_MESSAGE_TYPE ? "INLINE" : "ATTACHMENT",
			}),
			signal,
		});
	}

	async function executeQuery(message: CustomAppQueryMessage, signal: AbortSignal): Promise<unknown> {
		let response = await sendQuery(message, accessToken, signal);
		if (response.status === 401) {
			accessToken = await refreshToken();
			response = await sendQuery(message, accessToken, signal);
		}
		const body = await parseResponseBody(response);
		if (!response.ok) {
			throw new CustomAppQueryRequestError(
				isRecord(body) && typeof body.code === "string" ? body.code : "QUERY_FAILED",
				apiErrorMessage(body, response.status, "Data Query"),
			);
		}
		return body;
	}

	function sendQuery(message: CustomAppQueryMessage, token: string, signal: AbortSignal) {
		const url = new URL(`/api/v1/erp/dataQuery/${message.dataset}`, apiOrigin);
		return fetcher(url, {
			method: "POST",
			credentials: "omit",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
				"X-Client-Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
			body: JSON.stringify({ request: message.request }),
			signal,
		});
	}

	function refreshToken(): Promise<string> {
		refreshPromise ??= options.refreshDataAccessToken().finally(() => {
			refreshPromise = undefined;
		});
		return refreshPromise;
	}

	options.frame.addEventListener("load", connect, { once: true });

	return {
		beforeClose: () => lifecycle.beforeClose(),
		notifyClosed: () => lifecycle.notifyClosed(),
		ai,
		notifyWindowClosed(windowId) {
			if (!disposed) activePort?.postMessage({ type: WINDOW_CLOSED_MESSAGE_TYPE, windowId });
		},
		dispose() {
			disposed = true;
			lifecycle.dispose();
			ai.dispose();
			options.frame.removeEventListener("load", connect);
			activePort?.close();
			for (const request of requests.values()) request.abort();
			requests.clear();
		},
	};
}

function isWindowsMessage(message: unknown): message is CustomAppWindowsMessage {
	if (!isRecord(message) || message.type !== WINDOWS_MESSAGE_TYPE || !validRequestId(message.requestId)) return false;
	if (message.operation === "close") return typeof message.windowId === "string";
	return (
		message.operation === "open" &&
		isRecord(message.params) &&
		(message.width === undefined || typeof message.width === "number") &&
		(message.height === undefined || typeof message.height === "number")
	);
}

function isGoogleMapsMessage(message: unknown): message is CustomAppGoogleMapsMessage {
	if (!isRecord(message) || message.type !== GOOGLE_MAPS_MESSAGE_TYPE || !validRequestId(message.requestId))
		return false;
	if (message.operation === "getConfiguration") return true;
	if (message.operation === "optimizeRoute") {
		return (
			isRouteLocation(message.origin) &&
			isRouteLocation(message.destination) &&
			Array.isArray(message.stops) &&
			message.stops.length >= 1 &&
			message.stops.length <= MAX_ROUTE_STOPS &&
			message.stops.every(isRouteLocation)
		);
	}
	return (
		message.operation === "geocodeAddresses" &&
		Array.isArray(message.addresses) &&
		message.addresses.length <= MAX_GEOCODED_ADDRESSES &&
		message.addresses.every(isGeocodingAddress)
	);
}

function isRouteLocation(value: unknown): value is RouteLocation {
	return (
		isRecord(value) &&
		Object.keys(value).every((field) => field === "lat" || field === "lng") &&
		typeof value.lat === "number" &&
		Number.isFinite(value.lat) &&
		typeof value.lng === "number" &&
		Number.isFinite(value.lng)
	);
}

/** Checks the endpoint's route: one index per stop, and one leg more than there are stops. */
function isOptimizedRoute(value: unknown, stopCount: number): boolean {
	return (
		isRecord(value) &&
		Array.isArray(value.stopOrder) &&
		value.stopOrder.length === stopCount &&
		value.stopOrder.every((index) => Number.isInteger(index) && index >= 0 && index < stopCount) &&
		new Set(value.stopOrder).size === stopCount &&
		typeof value.distanceKilometers === "number" &&
		typeof value.travelTimeHours === "number" &&
		Array.isArray(value.legs) &&
		value.legs.length === stopCount + 1 &&
		value.legs.every(
			(leg) =>
				isRecord(leg) && typeof leg.distanceKilometers === "number" && typeof leg.travelTimeHours === "number",
		) &&
		(value.encodedPolyline === null || typeof value.encodedPolyline === "string") &&
		typeof value.googleMapsUrl === "string"
	);
}

const GEOCODING_ADDRESS_FIELDS = ["address1", "address2", "postalCode", "city", "country"];

function isGeocodingAddress(value: unknown): value is Record<string, unknown> {
	return (
		isRecord(value) &&
		Object.entries(value).every(
			([field, fieldValue]) =>
				GEOCODING_ADDRESS_FIELDS.includes(field) &&
				(fieldValue === undefined || fieldValue === null || typeof fieldValue === "string"),
		)
	);
}

function isCoordinatesOrNull(value: unknown): boolean {
	return value === null || (isRecord(value) && typeof value.lat === "number" && typeof value.lng === "number");
}

function isBrowserFileDownloadMessage(message: unknown): message is CustomAppBrowserFileDownloadMessage {
	return (
		isRecord(message) &&
		message.type === BROWSER_FILE_DOWNLOAD_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		message.file instanceof Blob &&
		typeof message.fileName === "string" &&
		message.fileName.trim() !== "" &&
		message.fileName.length <= 255 &&
		!/[\\/\u0000-\u001f\u007f]/.test(message.fileName) &&
		message.fileName !== "." &&
		message.fileName !== ".."
	);
}

function isUploadMessage(message: unknown): message is CustomAppUploadMessage {
	return (
		isRecord(message) &&
		message.type === UPLOAD_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		message.file instanceof Blob &&
		typeof message.fileName === "string" &&
		message.fileName !== "" &&
		!/[\\/\u0000\r\n]/.test(message.fileName)
	);
}

function postUploadError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: UPLOAD_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function isActionMessage(message: unknown): message is CustomAppActionMessage {
	return (
		isRecord(message) &&
		message.type === ACTION_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		isRecord(message.request) &&
		typeof message.request.idempotencyKey === "string" &&
		Array.isArray(message.request.steps)
	);
}

function postActionError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: ACTION_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function isPartConfigurationMessageType(
	message: unknown,
): message is Record<string, unknown> & { type: PartConfigurationMessageType } {
	return (
		isRecord(message) &&
		typeof message.type === "string" &&
		Object.hasOwn(PART_CONFIGURATION_OPERATIONS, message.type)
	);
}

function isPartConfigurationMessage(message: unknown): message is CustomAppPartConfigurationMessage {
	return (
		isPartConfigurationMessageType(message) &&
		validRequestId(message.requestId) &&
		isRecord(message.request) &&
		isRecord(message.request.target) &&
		PART_CONFIGURATION_OPERATIONS[message.type].isRequest(message.request)
	);
}

function isViewEventMessage(message: unknown): message is CustomAppViewEventMessage {
	return (
		isRecord(message) &&
		message.type === VIEW_EVENT_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		isRecord(message.request) &&
		typeof message.request.view === "string" &&
		typeof message.request.event === "string" &&
		Object.hasOwn(message.request, "payload")
	);
}

/** Binds the event's handler to its payload, or null when the view has no such event or the payload is invalid. */
function viewEventHandler(
	viewOverride: CustomAppViewOverride,
	event: string,
	payload: unknown,
): (() => Promise<unknown>) | null {
	const guards: Record<string, (payload: unknown) => boolean> = VIEW_EVENT_PAYLOAD_GUARDS[viewOverride.view];
	const handlers: Record<string, (payload: never) => Promise<unknown>> = viewOverride.handlers;
	if (!Object.hasOwn(guards, event) || !guards[event]!(payload)) return null;
	return () => handlers[event]!(payload as never);
}

function postPartConfigurationError(
	port: MessagePort,
	messageType: string,
	requestId: string,
	code: string,
	message: string,
) {
	port.postMessage({ type: `${messageType}-error`, requestId, error: { code, message } });
}

function optionalBoolean(value: unknown) {
	return value === undefined || typeof value === "boolean";
}

function isFunctionCallMessage(message: unknown): message is CustomAppFunctionCallMessage {
	return (
		isRecord(message) &&
		message.type === FUNCTION_CALL_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		typeof message.functionKey === "string" &&
		message.functionKey !== "" &&
		isRecord(message.input) &&
		(message.inputFiles === undefined || isFunctionInputFiles(message.inputFiles))
	);
}

function isFunctionRunMessage(message: unknown): message is CustomAppFunctionRunMessage {
	if (!isRecord(message) || message.type !== FUNCTION_RUN_MESSAGE_TYPE || !validRequestId(message.requestId))
		return false;
	switch (message.operation) {
		case "start":
			return (
				typeof message.functionKey === "string" &&
				message.functionKey !== "" &&
				isRecord(message.input) &&
				(message.inputFiles === undefined || isFunctionInputFiles(message.inputFiles)) &&
				typeof message.idempotencyKey === "string"
			);
		case "get":
		case "cancel":
			return Number.isInteger(message.runId);
		case "list":
			return (
				(message.functionKey === undefined || typeof message.functionKey === "string") &&
				(message.active === undefined || typeof message.active === "boolean") &&
				(message.limit === undefined || Number.isInteger(message.limit))
			);
		default:
			return false;
	}
}

function toFunctionInputFiles(inputFiles: CustomAppFunctionCallMessage["inputFiles"]) {
	return (inputFiles ?? []).map((field) => ({
		inputField: field.inputField,
		files: field.files.map((file) => ({
			fileHandle: { fullFileName: file.fullFileName },
			fileName: file.fileName,
			mimeType: file.mimeType,
		})),
	}));
}

/** Unlike function calls, a failed artifact request never means that the function failed. */
function artifactAccessErrorCode(status: number): string {
	switch (status) {
		case 400:
			return "INVALID_INPUT";
		case 403:
			return "FORBIDDEN";
		case 404:
			return "NOT_FOUND";
		default:
			return "FILE_ACCESS_FAILED";
	}
}

/** Lets apps distinguish a call they must not repeat unchanged from a function execution that failed. */
function functionErrorCode(status: number): string {
	switch (status) {
		case 400:
			return "INVALID_INPUT";
		case 403:
			return "FORBIDDEN";
		case 404:
			return "NOT_FOUND";
		case 409:
			return "DISABLED";
		case 422:
			return "IDEMPOTENCY_CONFLICT";
		default:
			return "FUNCTION_FAILED";
	}
}

function isStorageMessage(message: unknown): message is CustomAppStorageMessage {
	if (!isRecord(message) || message.type !== STORAGE_MESSAGE_TYPE || !validRequestId(message.requestId)) return false;
	const validScope = message.scope === "shared" || message.scope === "private";
	const validPath = typeof message.path === "string" && message.path !== "";
	const validIfMatch = message.ifMatch === null || typeof message.ifMatch === "string";
	switch (message.operation) {
		case "list":
			return validScope && typeof message.prefix === "string";
		case "read":
		case "view":
		case "download":
			return validScope && validPath;
		case "write":
			return (
				validScope &&
				validPath &&
				message.file instanceof Blob &&
				(message.contentType === null || typeof message.contentType === "string") &&
				validIfMatch &&
				typeof message.createOnly === "boolean"
			);
		case "delete":
			return validScope && validPath && validIfMatch;
		default:
			return false;
	}
}

function toStorageFileInfo(value: unknown): CustomAppStorageFileInfo {
	if (
		!isRecord(value) ||
		(value.scope !== "SHARED" && value.scope !== "PRIVATE") ||
		typeof value.path !== "string" ||
		(value.contentType !== null && typeof value.contentType !== "string") ||
		typeof value.sizeBytes !== "number" ||
		typeof value.etag !== "string" ||
		typeof value.lastModified !== "string"
	) {
		throw new Error("The app storage file metadata was invalid.");
	}
	return {
		scope: value.scope === "SHARED" ? "shared" : "private",
		path: value.path,
		contentType: value.contentType,
		sizeBytes: value.sizeBytes,
		etag: value.etag,
		lastModified: value.lastModified,
	};
}

function isFunctionArtifactMessage(message: unknown): message is CustomAppFunctionArtifactMessage {
	return (
		isRecord(message) &&
		message.type === FUNCTION_ARTIFACT_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		Number.isInteger(message.artifactId) &&
		Number(message.artifactId) > 0 &&
		(message.operation === "view" || message.operation === "download" || message.operation === "blob")
	);
}

/**
 * The managed file sources an app can name. Each type validates its business identifiers and has an endpoint that
 * authorizes them and creates access through the managed file route.
 */
const MANAGED_FILE_SOURCES = {
	partConfigurationPropertyOption: {
		endpoint: "/api/v1/erp/configurator/partConfigurations/createPropertyOptionFileAccess",
		isSource: (source: Record<string, unknown>) =>
			isRecord(source.target) &&
			(source.modelKey === undefined || (typeof source.modelKey === "string" && source.modelKey !== "")) &&
			[source.propertyName, source.optionKey, source.attributeName].every(
				(name) => typeof name === "string" && name !== "",
			),
	},
} satisfies Record<string, { endpoint: string; isSource: (source: Record<string, unknown>) => boolean }>;

type ManagedFileSourceType = keyof typeof MANAGED_FILE_SOURCES;

function isManagedFileMessage(message: unknown): message is CustomAppManagedFileMessage {
	return (
		isRecord(message) &&
		message.type === MANAGED_FILE_MESSAGE_TYPE &&
		validRequestId(message.requestId) &&
		(message.operation === "view" || message.operation === "download" || message.operation === "blob") &&
		isRecord(message.source) &&
		typeof message.source.type === "string" &&
		Object.hasOwn(MANAGED_FILE_SOURCES, message.source.type) &&
		MANAGED_FILE_SOURCES[message.source.type as ManagedFileSourceType].isSource(message.source)
	);
}

function postManagedFileError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: MANAGED_FILE_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function isFunctionInputFiles(value: unknown): boolean {
	return (
		Array.isArray(value) &&
		value.every(
			(field) =>
				isRecord(field) &&
				typeof field.inputField === "string" &&
				field.inputField !== "" &&
				Array.isArray(field.files) &&
				field.files.every(
					(file) =>
						isRecord(file) &&
						typeof file.fullFileName === "string" &&
						typeof file.fileName === "string" &&
						(file.mimeType === null || typeof file.mimeType === "string"),
				),
		)
	);
}

function postFunctionArtifactError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: FUNCTION_ARTIFACT_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function postFunctionError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: FUNCTION_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function isQueryMessage(message: unknown): message is CustomAppQueryMessage {
	if (!isRecord(message) || message.type !== QUERY_MESSAGE_TYPE) return false;
	return (
		validRequestId(message.requestId) &&
		typeof message.dataset === "string" &&
		/^(?:[a-z][a-z0-9]*(?:-[a-z0-9]+)*|[a-z][A-Za-z0-9]*)$/.test(message.dataset) &&
		isRecord(message.request)
	);
}

function isCancelMessage(message: unknown): message is CustomAppCancelMessage {
	return isRecord(message) && message.type === CANCEL_MESSAGE_TYPE && validRequestId(message.requestId);
}

function isDocumentMessage(message: unknown): message is CustomAppDocumentMessage {
	if (
		!isRecord(message) ||
		(message.type !== DOCUMENT_VIEW_MESSAGE_TYPE && message.type !== DOCUMENT_DOWNLOAD_MESSAGE_TYPE) ||
		!validRequestId(message.requestId)
	)
		return false;
	const hasDocumentId = Number.isInteger(message.documentId) && Number(message.documentId) > 0;
	const hasRevisionId = Number.isInteger(message.documentRevisionId) && Number(message.documentRevisionId) > 0;
	return hasDocumentId !== hasRevisionId;
}

function isDocumentMessageType(message: unknown): boolean {
	return (
		isRecord(message) &&
		(message.type === DOCUMENT_VIEW_MESSAGE_TYPE || message.type === DOCUMENT_DOWNLOAD_MESSAGE_TYPE)
	);
}

function isFileAccess(value: unknown, runtimeOrigin: string, pathPrefix: string): value is CustomAppDocumentAccess {
	if (
		!isRecord(value) ||
		typeof value.url !== "string" ||
		typeof value.fileName !== "string" ||
		typeof value.contentType !== "string"
	)
		return false;
	try {
		const url = new URL(value.url);
		return (
			url.origin === runtimeOrigin &&
			url.username === "" &&
			url.password === "" &&
			url.pathname.startsWith(pathPrefix)
		);
	} catch {
		return false;
	}
}

function validRequestId(value: unknown): value is string {
	return typeof value === "string" && value.length > 0 && value.length <= 128;
}

function requestIdOf(message: unknown): string {
	return isRecord(message) && validRequestId(message.requestId) ? message.requestId : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function postError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: QUERY_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function postDocumentError(port: MessagePort, requestId: string, code: string, message: string) {
	port.postMessage({ type: DOCUMENT_ERROR_MESSAGE_TYPE, requestId, error: { code, message } });
}

function postInvalidRequest(port: MessagePort, message: unknown) {
	const requestId = requestIdOf(message);
	if (isRecord(message) && message.type === BROWSER_FILE_DOWNLOAD_MESSAGE_TYPE) {
		port.postMessage({
			type: BROWSER_FILE_DOWNLOAD_ERROR_MESSAGE_TYPE,
			requestId,
			error: { code: "INVALID_REQUEST", message: "Invalid Custom App file download request." },
		});
	} else if (isRecord(message) && message.type === UPLOAD_MESSAGE_TYPE) {
		postUploadError(port, requestId, "INVALID_REQUEST", "Invalid Custom App upload request.");
	} else if (isRecord(message) && message.type === ACTION_MESSAGE_TYPE) {
		postActionError(port, requestId, "INVALID_REQUEST", "Invalid Custom App action request.");
	} else if (isRecord(message) && message.type === FUNCTION_CALL_MESSAGE_TYPE) {
		postFunctionError(port, requestId, "INVALID_REQUEST", "Invalid Custom App function call request.");
	} else if (isRecord(message) && message.type === FUNCTION_ARTIFACT_MESSAGE_TYPE) {
		postFunctionArtifactError(port, requestId, "INVALID_REQUEST", "Invalid Custom App function artifact request.");
	} else if (isRecord(message) && message.type === MANAGED_FILE_MESSAGE_TYPE) {
		postManagedFileError(port, requestId, "INVALID_REQUEST", "Invalid Custom App managed file request.");
	} else if (isRecord(message) && message.type === STORAGE_MESSAGE_TYPE) {
		port.postMessage({
			type: STORAGE_ERROR_MESSAGE_TYPE,
			requestId,
			error: { code: "INVALID_REQUEST", message: "Invalid Custom App storage request." },
		});
	} else if (isRecord(message) && message.type === GOOGLE_MAPS_MESSAGE_TYPE) {
		port.postMessage({
			type: GOOGLE_MAPS_ERROR_MESSAGE_TYPE,
			requestId,
			error: { code: "INVALID_REQUEST", message: "Invalid Custom App Google Maps request." },
		});
	} else if (isRecord(message) && message.type === WINDOWS_MESSAGE_TYPE) {
		port.postMessage({
			type: WINDOWS_ERROR_MESSAGE_TYPE,
			requestId,
			error: { code: "INVALID_REQUEST", message: "Invalid Custom App window request." },
		});
	} else if (isRecord(message) && message.type === VIEW_EVENT_MESSAGE_TYPE) {
		port.postMessage({
			type: VIEW_EVENT_ERROR_MESSAGE_TYPE,
			requestId,
			error: { code: "INVALID_REQUEST", message: "Invalid Custom App view event." },
		});
	} else if (isPartConfigurationMessageType(message)) {
		const description = PART_CONFIGURATION_OPERATIONS[message.type].description.toLowerCase();
		postPartConfigurationError(
			port,
			message.type,
			requestId,
			"INVALID_REQUEST",
			`Invalid Custom App ${description} request.`,
		);
	} else if (isDocumentMessageType(message)) {
		postDocumentError(port, requestId, "INVALID_REQUEST", "Invalid Custom App document request.");
	} else {
		postError(port, requestId, "INVALID_REQUEST", "Invalid Custom App query request.");
	}
}

function startDownload(access: CustomAppDocumentAccess) {
	downloadFileUrl(access.fileName, access.url, { openInNewTab: false });
}

function documentViewAccess(access: CustomAppDocumentAccess): CustomAppDocumentAccess {
	if (access.contentType !== "application/pdf") return access;
	// The viewer bootstrap moves this fragment into its in-document query state after navigation. Keeping the
	// capability in the fragment prevents the viewer page request and its referrer from exposing the token.
	const fragment = new URLSearchParams({ file: access.url });
	const viewer = new URL("/custom-app-runtime/pdfjs/web/viewer.html", access.url);
	viewer.hash = fragment.toString();
	return { ...access, url: viewer.href };
}

async function parseResponseBody(response: Response): Promise<unknown> {
	const text = await response.text();
	if (text === "") return null;
	try {
		return JSON.parse(text) as unknown;
	} catch {
		if (!response.ok) return null;
		throw new Error("The Data Query response was not valid JSON.");
	}
}

function apiErrorMessage(body: unknown, status: number, operation: string): string {
	if (isRecord(body) && typeof body.message === "string" && body.message !== "") return body.message;
	return `${operation} failed with HTTP status ${status}.`;
}

function errorMessage(error: unknown, fallback = "Data Query failed."): string {
	return error instanceof Error && error.message !== "" ? error.message : fallback;
}
